import { FEEDBACK_ITEM_COLUMNS, parseConfirmFeedbackArgs, requireFeedbackConfirmationReceipt, parseFeedbackCompletions } from '@cerebro-diem/core';
import type { FeedbackItem, PendingFeedback, ConfirmFeedbackArgs, ConfirmFeedbackReceipt, FeedbackCompletion } from '@cerebro-diem/core';
import { LIST_LIMIT } from './queries';
import { getSession, supabase } from './supabase';

/** This build's version, set at build time (vite.config.ts): the package version and, on Vercel, the commit. */
export const APP_VERSION = __APP_VERSION__;

/** Pin the original author's bearer on this request; never change global client headers. */
export async function sendFeedback(entry: PendingFeedback): Promise<string> {
  const { data: { session } } = await getSession();
  if (!session || session.user.id !== entry.ownerId) throw new Error('The signed-in account changed; your waiting report is kept for its author');
  const { row } = entry;
  const { data, error } = await supabase.rpc('submit_feedback', {
    p_id: entry.id, p_kind: row.kind, p_message: row.message, p_screen: row.screen,
    p_app_version: row.app_version, p_platform: row.platform,
  }).setHeader('Authorization', `Bearer ${session.access_token}`);
  if (error) throw error;
  if (data !== entry.id) throw new Error('The server receipt did not match; your waiting report is kept');
  return data;
}

/** What the signed-in user has sent, newest first, with where each stands. RLS returns only their own. */
export async function listMyFeedback(ownerId: string): Promise<FeedbackItem[]> {
  const { data: { session } } = await getSession();
  if (!session || session.user.id !== ownerId) throw new Error('The signed-in account changed; feedback history was not loaded');
  const { data, error } = await supabase
    .from('feedback')
    .select(FEEDBACK_ITEM_COLUMNS)
    .order('created_at', { ascending: false })
    .limit(LIST_LIMIT)
    .setHeader('Authorization', `Bearer ${session.access_token}`);
  if (error) throw error;
  return (data ?? []) as FeedbackItem[];
}

/** Completion history belongs to the validated session; pin its bearer on this RPC only. */
export async function listMyFeedbackCompletions(expectedOwnerId: string): Promise<FeedbackCompletion[]> {
  const { data: { session } } = await getSession();
  if (!session?.access_token || session.user.id !== expectedOwnerId) throw new Error('The signed-in account changed; completion history was not loaded');
  const { data, error } = await supabase.rpc('list_my_feedback_completions')
    .setHeader('Authorization', `Bearer ${session.access_token}`);
  if (error) throw error;
  return parseFeedbackCompletions(data);
}

/** An immutable confirmation is acknowledged only by its exact UUID receipt. */
export async function confirmFeedback(expectedOwnerId: string, input: ConfirmFeedbackArgs): Promise<ConfirmFeedbackReceipt> {
  const args = parseConfirmFeedbackArgs(input);
  const { data: { session } } = await getSession();
  if (!session?.access_token || session.user.id !== expectedOwnerId) throw new Error('The signed-in account changed; your confirmation was not sent');
  const { data, error } = await supabase.rpc('confirm_feedback', args)
    .setHeader('Authorization', `Bearer ${session.access_token}`);
  if (error) throw error;
  return requireFeedbackConfirmationReceipt(args, data);
}
